import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useKeyOperation } from 'components/WorkspaceEditor/features/keyOperations/hooks/useKeyOperation'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { useContentEditorFocus } from 'components/WorkspaceEditor/hooks/useContentEditorFocus'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNodeMapShallowSelector } from '../../features/keyOperations/store/NodeMapStore'
import { useShallowSelector } from '../../Workspace2Store'
import {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../ContentEditor'
import {
    extractSuggestions,
    getUnresolvedChanges,
} from '../ContentEditor/contentUtils'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'

/** Event handlers for ReplacementOption CKEditor interactions */
interface ReplacementOptionHandler {
    /**
     * Handler called when CKEditor is ready and initialized.
     * Sets the editor reference for transaction management and rollback capabilities.
     */
    onReady: ClassicEditorOnReadyHandler

    /**
     * Handler called when CKEditor loses focus.
     * - Deactivates the editor when there are no content changes
     * - Processes and saves changes when content has been modified
     * - Triggers optimistic mutations for immediate UI updates
     */
    onBlur: ClassicEditorHandler

    /**
     * Handler for keyboard events while CKEditor has focus.
     * Handles special key combinations:
     * - Enter: Save current content and add new content
     * - Ctrl+S: Save content only
     * - Escape: Deactivate editor and revert changes
     */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void

    /**
     * Handler called when CKEditor content changes.
     */
    onChange: ClassicEditorHandler

    /**
     * Handler called when CKEditor receives focus.
     * Activates the editor to enable content editing.
     */
    onFocusCapture: ClassicEditorHandler
}

interface UseFormatReplacementOptionReturn {
    handlers: ReplacementOptionHandler
    ckeToggle: ReturnType<typeof useCKEditorToggle<string>>
}

/**
 * A hook that provides controller functionality for replacement options in the editor.
 * It handles focus, blur, and change events for a replacement option component.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item representing the replacement option
 * @param {Function} [onValueChange] - Optional callback that's called when the content item's value changes
 * @returns {Object} An object containing event handlers for the replacement option component
 */
export const useReplacementOptionController = (
    contentItem: Partial<TextContentElement>,
    onValueChange?: (
        currentSelectedItem: Partial<TextContentElement>,
        currentlySelected: boolean
    ) => void
): UseFormatReplacementOptionReturn => {
    const { registerEditorRef, editorRef } = useTransactionManager()
    const {
        getUpdatedContentItem,
        removeUnitOfMeasureDelimiters,
    } = useFormatReplacementOption(contentItem)
    // State to track unsaved content changes in the editor
    const [unsavedData, setUnsavedData] = useState<string>(
        contentItem.text ?? ''
    )

    // Flag to determine if new content should be added after saving
    const [shouldTriggerNewContent, setShouldTriggerNewContent] = useState(
        false
    )
    // Create unique identifier for editor tracking - no memoization needed for simple string concatenation
    const formattedContentId = `replacement-option-${
        contentItem.id || 'default'
    }`
    const ckeToggle = useCKEditorToggle(formattedContentId)

    // Track activation time to prevent premature blur handling
    const activationTimestampRef = useRef<number>(0)

    // Get save operation state to prevent blur during saves
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    // Get tree mapping state and deactivation action
    const { isTreeMappingInProgress, deactivateAllEditors } = useShallowSelector((s) => ({
        isTreeMappingInProgress: s.isTreeMappingInProgress,
        deactivateAllEditors: s.actions.deactivateAllEditors,
    }))

    const initialSuggestions = useMemo(
        () => extractSuggestions([contentItem]),
        [contentItem.id, contentItem.changeType]
    )

    const { editorFocusRef, focusEditor } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
        controlType: 'replacementOption',
    })

    /**
     * Handles the focus capture event for the replacement option.
     * Activates the CKEditor when the element receives focus.
     * Records activation time to prevent race conditions with rapid clicks.
     * Deactivates all other editors first to ensure only one is active at a time.
     */
    const handleOnFocusCapture = useCallback((): void => {
        // Deactivate all other editors first
        deactivateAllEditors()

        // Use requestAnimationFrame to ensure deactivation completes before activation
        requestAnimationFrame(() => {
            // Record activation time for race condition prevention
            activationTimestampRef.current = Date.now()
            // Use the new focusEditor function instead of directly activating
            focusEditor()
        })
    }, [focusEditor, deactivateAllEditors])

    /**
     * Handles the blur event for the CKEditor.
     * Extracts plain text from the editor's HTML content, updates the content item if changed,
     * and calls the onValueChange callback if provided.
     *
     * @param {FocusEvent} event - The blur event
     * @param {Object} editor - The CKEditor instance
     */
    /**
     * Handles the blur event when CKEditor loses focus.
     * Processes content changes, updates the content item, and deactivates the editor.
     *
     * Race Condition Protection:
     * - Blocks blur processing during save operations to prevent conflicts
     * - Blocks blur if activated too recently (within 100ms grace period)
     * - Prevents concurrent blur/activation race conditions
     *
     * Change detection logic:
     * - Content is considered unchanged ONLY if BOTH conditions are true:
     *   1. Current content matches original text
     *   2. Original text matches placeholder text
     *
     * This prevents premature deactivation when users click into an already-edited
     * replacement option (where text != placeholderText).
     */
    const handleBlur = useCallback(
        (_, editor): void => {
            // RACE CONDITION PROTECTION: Block blur during critical operations
            const isSystemBusy =
                isSaveOperationInProgress || (isTreeMappingInProgress ?? false)

            // Check if activation happened too recently (within 100ms grace period)
            const activationAge = Date.now() - activationTimestampRef.current
            const isActivationTooRecent = activationAge < 100

            // Block blur processing if system is busy or activation is too recent
            if (isSystemBusy || isActivationTooRecent) {
                return
            }

            const currentContent = unsavedData

            // Defensive null checks to prevent false positives in change detection
            const textValue = contentItem?.text ?? ''
            const placeholderValue = contentItem?.placeholderText ?? ''

            // Check if user actually typed anything
            const nothingTyped = currentContent === '' && textValue === ''

            // Only consider content unchanged if BOTH:
            // 1. Current content matches original text
            // 2. Original text matches placeholder (meaning it was never edited)
            // OR if nothing was typed (empty field, just clicked in and out)
            const hasNoChanges =
                nothingTyped ||
                (currentContent === textValue && textValue === placeholderValue)

            // Early return if no changes were made - simply deactivate the editor
            if (hasNoChanges) {
                return ckeToggle.deactivate()
            }

            // Handle empty content by falling back to placeholder text if available
            const isContentEmpty = currentContent.trim() === ''
            const hasPlaceholderText =
                contentItem.placeholderText != null &&
                contentItem.placeholderText !== ''
            const finalText =
                isContentEmpty && hasPlaceholderText
                    ? contentItem.placeholderText!
                    : currentContent

            // Create updated content item with processed text and elements
            const updatedContentItem: Partial<TextContentElement> = {
                ...contentItem,
                text: removeUnitOfMeasureDelimiters(finalText),
                placeholderText: removeUnitOfMeasureDelimiters(
                    contentItem.placeholderText
                ),
                elements: getUpdatedContentItem(contentItem),
            }

            // Notify parent component of the content change
            onValueChange?.(updatedContentItem, false)

            // Deactivate the editor
            ckeToggle.deactivate()
        },
        [
            contentItem,
            onValueChange,
            ckeToggle,
            unsavedData,
            removeUnitOfMeasureDelimiters,
            getUpdatedContentItem,
            isSaveOperationInProgress,
            isTreeMappingInProgress,
        ]
    )
    const {
        handleDeactivateAndAddContentReplacementOption,
        executeSaveAndAddContent,
        executeSaveOnly,
    } = useKeyOperation({
        editorRef,
        contentItemId: contentItem.id,
        handleOnBlur: handleBlur,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        unFocusEditor: ckeToggle.deactivate,
    })

    const handleOnChange = useCallback(
        (_, editor): void => {
            const hasInitialSuggestions = initialSuggestions.length > 0

            const textChanges = getUnresolvedChanges(
                editor.getData(),
                contentItem.text,
                hasInitialSuggestions
            )
            setUnsavedData(textChanges)
        },
        [initialSuggestions, contentItem.text]
    )

    /**
     * Handles keyboard events for the replacement option.
     * Manages save operations, content addition, and editor deactivation based on key combinations.
     */
    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>): void => {
            // Handle save-only operations (e.g., Ctrl+S)
            if (executeSaveOnly(event)) {
                return
            }

            // Determine if content has been modified
            const hasNoChanges = unsavedData === contentItem.text

            // Set flag to indicate new content should be triggered
            setShouldTriggerNewContent(true)

            // Case 1: Handle immediate deactivation and content addition (e.g., Escape key)
            if (
                handleDeactivateAndAddContentReplacementOption(
                    event,
                    hasNoChanges
                )
            ) {
                return
            }

            // Case 2: Handle save and add new content (e.g., Enter key)
            if (executeSaveAndAddContent(event)) {
                return
            }

            // Reset the trigger flag if no special operation was performed
            setShouldTriggerNewContent(false)
        },
        [
            executeSaveOnly,
            unsavedData,
            contentItem.text,
            handleDeactivateAndAddContentReplacementOption,
            executeSaveAndAddContent,
        ]
    )

    // Create onReady handler that sets up editor references
    const onReady = useCallback(
        (editor): void => {
            editorFocusRef.current = editor
            registerEditorRef(editor)
        },
        [editorFocusRef, registerEditorRef]
    )

    // Return individual handler functions - simpler than memoized object
    return {
        handlers: {
            onReady,
            onChange: handleOnChange,
            onBlur: handleBlur,
            onFocusCapture: handleOnFocusCapture,
            onKeyDown: handleOnKeyDown,
        },
        ckeToggle, // Return the toggle so ReplacementOption can use it
    }
}
